{
  "$schema": "https://cogo.xyz/ontology/v1/element-types",
  "version": "2.0.0",
  "description": "Canonical registry of all valid Cogo JSON element types with property-level specs",
  "commonProperties": {
    "id": {
      "type": "string",
      "required": true,
      "description": "Unique element ID within block"
    },
    "type": {
      "type": "string",
      "required": true,
      "description": "Element type name"
    },
    "className": {
      "type": "string",
      "description": "Tailwind CSS classes (primary styling method)"
    },
    "children": {
      "type": "array",
      "items": "CogoElement",
      "description": "Child elements"
    },
    "iterateOver": {
      "type": "string",
      "description": "Symbol path to array for iteration, e.g. #{items}"
    },
    "itemVar": {
      "type": "string",
      "description": "Variable name for iteration item"
    },
    "visibleWhen": {
      "type": "string",
      "description": "Condition expression for conditional visibility"
    },
    "hidden": {
      "type": "boolean",
      "default": false,
      "description": "Hide element"
    },
    "events": {
      "type": "object",
      "description": "Event bindings: { eventType: { flow, params } }"
    },
    "animation": {
      "type": "object",
      "description": "Enter/exit/scroll animations"
    },
    "layout": {
      "type": "object",
      "description": "Layout configuration for container-like elements",
      "properties": {
        "type": {
          "type": "enum",
          "values": ["flex", "grid"],
          "description": "Layout mode"
        },
        "direction": {
          "type": "enum",
          "values": ["row", "column"],
          "default": "column"
        },
        "align": {
          "type": "enum",
          "values": ["start", "center", "end", "stretch", "baseline"]
        },
        "justify": {
          "type": "enum",
          "values": ["start", "center", "end", "between", "around", "evenly"]
        },
        "gap": {
          "type": "string",
          "description": "Gap value, e.g. '1rem' or '16'"
        },
        "wrap": {
          "type": "boolean"
        },
        "columns": {
          "type": "number|string",
          "description": "Grid columns count or template"
        },
        "responsive": {
          "type": "object",
          "description": "Breakpoint overrides: { sm: {...}, md: {...}, lg: {...} }"
        }
      }
    },
    "position": {
      "type": "object",
      "description": "CSS positioning",
      "properties": {
        "type": {
          "type": "enum",
          "values": ["relative", "absolute", "sticky", "fixed"]
        },
        "top": {
          "type": "string"
        },
        "right": {
          "type": "string"
        },
        "bottom": {
          "type": "string"
        },
        "left": {
          "type": "string"
        }
      }
    },
    "padding": {
      "type": "string",
      "description": "Padding value or Tailwind class"
    },
    "margin": {
      "type": "string",
      "description": "Margin value or Tailwind class"
    }
  },
  "types": {
    "container": {
      "category": "layout",
      "description": "Generic wrapper element with layout, padding, and styling",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "layout",
        "padding",
        "display",
        "gridCols",
        "gridColsResponsive"
      ],
      "canHaveChildren": true,
      "tags": ["box", "div", "wrapper", "frame"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["card", "bordered"],
          "description": "Visual variant"
        },
        "fill": {
          "type": "boolean",
          "description": "Fill available width"
        },
        "display": {
          "type": "enum",
          "values": ["flex", "grid", "block", "none"],
          "default": "flex"
        },
        "gridCols": {
          "type": "number",
          "description": "Number of grid columns"
        },
        "gridColsResponsive": {
          "type": "object",
          "description": "Responsive grid columns: { sm: 1, md: 2, lg: 3 }"
        },
        "gap": {
          "type": "string",
          "description": "Gap between children"
        },
        "maxWidth": {
          "type": "string",
          "description": "Max width constraint"
        },
        "minHeight": {
          "type": "string",
          "description": "Min height"
        },
        "aspectRatio": {
          "type": "string",
          "description": "Aspect ratio, e.g. '16/9'"
        },
        "overflow": {
          "type": "enum",
          "values": ["hidden", "auto", "scroll", "visible"],
          "default": "visible"
        },
        "opacity": {
          "type": "number",
          "description": "0-1 opacity"
        },
        "background": {
          "type": "object",
          "description": "{ color: string }"
        },
        "border": {
          "type": "object",
          "description": "{ color: string, width: number }"
        },
        "alignment": {
          "type": "enum",
          "values": [
            "topLeft",
            "topCenter",
            "topRight",
            "centerLeft",
            "center",
            "centerRight",
            "bottomLeft",
            "bottomCenter",
            "bottomRight"
          ],
          "default": "center",
          "description": "Child alignment (LIVE CgContainerPropertyMetadata 9-value enum)"
        }
      }
    },
    "text": {
      "category": "basic",
      "description": "Renders static or dynamic text content",
      "requiredProps": [],
      "optionalProps": ["text", "tag", "className", "tone"],
      "canHaveChildren": false,
      "tags": ["typography", "label", "heading", "paragraph"],
      "properties": {
        "text": {
          "type": "string",
          "description": "Text content (supports #{symbol} interpolation)"
        },
        "tag": {
          "type": "enum",
          "values": ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "label"],
          "description": "HTML semantic tag"
        },
        "variant": {
          "type": "enum",
          "values": [
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "p",
            "caption",
            "heading-xl",
            "heading-lg",
            "heading-md"
          ],
          "description": "Typography variant"
        },
        "size": {
          "type": "enum",
          "values": ["xs", "sm", "base", "lg", "xl", "2xl", "3xl"],
          "description": "Font size variant (semantic-types-v1.ttl)"
        },
        "weight": {
          "type": "enum",
          "values": ["normal", "medium", "semibold", "bold"],
          "default": "normal",
          "description": "Font weight override"
        },
        "tone": {
          "type": "enum",
          "values": ["default", "muted", "primary", "destructive", "success", "warning", "info"],
          "description": "tone (semantic-types-v1.ttl)"
        },
        "color": {
          "type": "string",
          "description": "Text color"
        },
        "align": {
          "type": "enum",
          "values": ["left", "center", "right"],
          "description": "Text alignment"
        },
        "lineHeight": {
          "type": "string",
          "description": "Line height"
        },
        "letterSpacing": {
          "type": "string",
          "description": "Letter spacing"
        },
        "lineClamp": {
          "type": "number",
          "description": "Max number of lines before truncation"
        },
        "fontSize": {
          "type": "number",
          "default": 14,
          "description": "Font size in px (LIVE CgTextPropertyMetadata, min 1 max 200)"
        },
        "fontWeight": {
          "type": "enum",
          "values": [
            "w100",
            "w200",
            "w300",
            "w400",
            "w500",
            "w600",
            "w700",
            "w800",
            "w900"
          ],
          "default": "w400",
          "description": "Font weight (LIVE CgTextPropertyMetadata 9-step enum)"
        },
        "textAlignment": {
          "type": "enum",
          "values": ["start", "center", "end", "left", "right", "justify"],
          "default": "start",
          "description": "Text alignment (LIVE CgTextPropertyMetadata enum)"
        }
      }
    },
    "button": {
      "category": "basic",
      "interactive": true,
      "description": "Interactive button with variant, size, and event handler",
      "requiredProps": [],
      "optionalProps": [
        "text",
        "variant",
        "size",
        "icon",
        "className",
        "disabled"
      ],
      "canHaveChildren": true,
      "tags": ["action", "click", "submit", "cta"],
      "properties": {
        "text": {
          "type": "string",
          "description": "Button label text"
        },
        "variant": {
          "type": "enum",
          "values": [
            "default",
            "secondary",
            "outline",
            "ghost",
            "link",
            "destructive"
          ],
          "default": "default"
        },
        "size": {
          "type": "enum",
          "values": ["default", "sm", "lg", "icon", "icon-sm", "icon-xs"],
          "default": "default"
        },
        "tone": {
          "type": "string",
          "default": "primary",
          "description": "Semantic tone/token key"
        },
        "icon": {
          "type": "string",
          "description": "Lucide icon name"
        },
        "iconPosition": {
          "type": "enum",
          "values": ["left", "right"],
          "default": "left"
        },
        "disabled": {
          "type": "boolean",
          "default": false
        },
        "loading": {
          "type": "boolean",
          "default": false
        },
        "href": {
          "type": "string",
          "description": "Makes button a link"
        },
        "target": {
          "type": "enum",
          "values": ["_self", "_blank"]
        },
        "fill": {
          "type": "boolean",
          "description": "Full-width button"
        },
        "htmlType": {
          "type": "enum",
          "values": ["button", "submit", "reset"],
          "default": "button",
          "description": "Native <button type> the renderer emits (button-renderer resolveHtmlButtonType). Omit it, or use \"button\", on any button that carries a tap event binding: \"submit\" inside a submit-bound form opens both doors and runs the flow twice. See docs/agentic/HTMLTYPE-DOOR-DECISION-2026-08-26.md."
        }
      }
    },
    "video": {
      "category": "basic",
      "description": "Renders an HTML5 <video>; auto-registers with the host MediaRegistry for the mediaControl action",
      "requiredProps": ["src"],
      "optionalProps": [
        "poster",
        "controls",
        "muted",
        "autoplay",
        "loop",
        "className"
      ],
      "canHaveChildren": false,
      "tags": ["media", "player"],
      "properties": {
        "src": {
          "type": "string",
          "required": true,
          "description": "Video URL or asset path"
        },
        "poster": {
          "type": "string",
          "description": "Poster image shown before playback"
        },
        "controls": {
          "type": "boolean",
          "default": true,
          "description": "Show native playback controls"
        },
        "muted": {
          "type": "boolean",
          "default": false,
          "description": "Start muted"
        },
        "autoplay": {
          "type": "boolean",
          "default": false,
          "description": "Start playback automatically"
        },
        "loop": {
          "type": "boolean",
          "default": false,
          "description": "Loop playback"
        }
      }
    },
    "image": {
      "category": "basic",
      "description": "Displays an image from URL or asset",
      "requiredProps": ["src"],
      "optionalProps": ["alt", "className", "fit"],
      "canHaveChildren": false,
      "tags": ["picture", "photo", "media"],
      "properties": {
        "src": {
          "type": "string",
          "required": true,
          "description": "Image URL or asset path"
        },
        "alt": {
          "type": "string",
          "description": "Alt text for accessibility"
        },
        "fit": {
          "type": "enum",
          "values": ["cover", "contain", "fill", "none", "scale-down"],
          "default": "cover"
        },
        "width": {
          "type": "string",
          "description": "Width (CSS value or number)"
        },
        "height": {
          "type": "string",
          "description": "Height (CSS value or number)"
        },
        "aspectRatio": {
          "type": "string",
          "description": "Aspect ratio, e.g. '1/1', '16/9'"
        },
        "rounded": {
          "type": "boolean",
          "description": "Apply rounded corners"
        }
      }
    },
    "iframe": {
      "category": "basic",
      "description": "Embedded iframe preview or external content surface",
      "requiredProps": [],
      "optionalProps": [
        "src",
        "srcDoc",
        "title",
        "ariaLabel",
        "label",
        "allow",
        "sandbox",
        "referrerPolicy",
        "loading",
        "className",
        "style"
      ],
      "canHaveChildren": false,
      "tags": ["embed", "preview", "web"],
      "properties": {
        "src": {
          "type": "string",
          "description": "Iframe source URL or binding."
        },
        "srcDoc": {
          "type": "string",
          "description": "Inline iframe HTML or binding."
        },
        "title": {
          "type": "string",
          "description": "Accessible iframe title."
        },
        "ariaLabel": {
          "type": "string",
          "description": "Fallback accessible label."
        },
        "label": {
          "type": "string",
          "description": "Fallback visible/accessibility label."
        },
        "allow": {
          "type": "string",
          "description": "Feature policy allow attribute."
        },
        "sandbox": {
          "type": "string",
          "description": "Sandbox token list."
        },
        "referrerPolicy": {
          "type": "string",
          "description": "Referrer policy attribute."
        },
        "loading": {
          "type": "enum",
          "values": ["lazy", "eager"],
          "description": "Native iframe loading mode."
        }
      }
    },
    "icon": {
      "category": "basic",
      "description": "Renders a Lucide icon by name",
      "requiredProps": ["icon"],
      "optionalProps": ["name", "className", "size", "tone"],
      "canHaveChildren": false,
      "tags": ["glyph", "symbol", "lucide"],
      "properties": {
        "icon": {
          "type": "string",
          "required": true,
          "description": "Lucide icon name, e.g. 'ChevronRight', 'Search'"
        },
        "name": {
          "type": "string",
          "aliasFor": "icon",
          "description": "Alias for icon (fallback). icon-renderer.tsx resolves `element.name ?? element.icon`, preferring this spelling, so an icon carrying only `name` renders. `aliasFor` is the machine-readable form of that fact, read by `element-required-prop`."
        },
        "size": {
          "type": "enum",
          "values": ["xs", "sm", "base", "lg", "xl"],
          "description": "Icon size variant (semantic-types-v1.ttl)"
        },
        "tone": {
          "type": "enum",
          "values": ["default", "muted", "primary", "destructive", "success", "warning"],
          "description": "tone (semantic-types-v1.ttl)"
        },
        "color": {
          "type": "string",
          "description": "Icon color"
        }
      }
    },
    "input": {
      "category": "form",
      "description": "Single-line text input field",
      "requiredProps": ["bind"],
      "optionalProps": [
        "placeholder",
        "inputType",
        "className",
        "icon",
        "label",
        "variant",
        "size",
        "hint"
      ],
      "canHaveChildren": false,
      "tags": ["textfield", "form-control"],
      "properties": {
        "bind": {
          "type": "string",
          "required": true,
          "description": "State variable path for two-way binding"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "outline"],
          "default": "outline",
          "description": "Input render variant (LIVE CgInputPropertyMetadata enum; default \"outline\")"
        },
        "size": {
          "type": "enum",
          "values": ["default", "sm", "lg"],
          "default": "default",
          "description": "Input size (semantic-types-v1.ttl / shadcn preset)"
        },
        "tone": {
          "type": "string",
          "description": "Semantic tone/token key (LIVE CgInputPropertyMetadata)"
        },
        "hint": {
          "type": "string",
          "default": "",
          "description": "Placeholder hint text (LIVE CgInputPropertyMetadata)"
        },
        "obscureText": {
          "type": "boolean",
          "default": false,
          "description": "Obscure entered text, e.g. password (LIVE CgInputPropertyMetadata)"
        },
        "intOnly": {
          "type": "boolean",
          "default": false,
          "description": "Restrict input to integer characters (LIVE CgInputPropertyMetadata)"
        },
        "readOnly": {
          "type": "boolean",
          "default": false,
          "description": "Render the field read-only (LIVE CgInputPropertyMetadata)"
        },
        "placeholder": {
          "type": "string",
          "description": "Placeholder text"
        },
        "inputType": {
          "type": "enum",
          "values": [
            "text",
            "email",
            "password",
            "number",
            "tel",
            "url",
            "search",
            "date",
            "time",
            "datetime-local"
          ],
          "default": "text"
        },
        "label": {
          "type": "string",
          "description": "Input label text"
        },
        "icon": {
          "type": "string",
          "description": "Lucide icon name for input adornment"
        },
        "required": {
          "type": "boolean",
          "default": false
        },
        "disabled": {
          "type": "boolean",
          "default": false
        },
        "pattern": {
          "type": "string",
          "description": "Regex validation pattern"
        },
        "min": {
          "type": "number",
          "description": "Minimum value (for number type)"
        },
        "max": {
          "type": "number",
          "description": "Maximum value (for number type)"
        },
        "maxLength": {
          "type": "number",
          "min": 0,
          "max": 10000,
          "description": "Maximum character length (LIVE CgInputPropertyMetadata range 0..10000)"
        }
      }
    },
    "textarea": {
      "category": "form",
      "description": "Multi-line text input area",
      "requiredProps": ["bind"],
      "optionalProps": ["placeholder", "rows", "className", "size"],
      "canHaveChildren": false,
      "tags": ["multiline", "text-area"],
      "properties": {
        "bind": {
          "type": "string",
          "required": true,
          "description": "State variable path for two-way binding"
        },
        "placeholder": {
          "type": "string",
          "description": "Placeholder text"
        },
        "rows": {
          "type": "number",
          "default": 3,
          "description": "Visible row count"
        },
        "size": {
          "type": "enum",
          "values": ["default", "sm", "lg"],
          "description": "size (semantic-types-v1.ttl)"
        },
        "text": {
          "type": "string",
          "description": "Initial text value"
        },
        "required": {
          "type": "boolean",
          "default": false
        },
        "disabled": {
          "type": "boolean",
          "default": false
        }
      }
    },
    "select": {
      "category": "smart",
      "interactive": true,
      "description": "Dropdown selection from options — open/close, keyboard nav, a11y built-in",
      "builtinState": ["selectedValue", "isOpen"],
      "preferOver": "container with dropdown toggle pattern",
      "requiredProps": ["bind"],
      "optionalProps": ["options", "placeholder", "className", "size"],
      "canHaveChildren": false,
      "tags": ["dropdown", "picker"],
      "properties": {
        "bind": {
          "type": "string",
          "required": true,
          "description": "State variable path for two-way binding"
        },
        "options": {
          "type": "array",
          "description": "Array of { label, value } objects"
        },
        "placeholder": {
          "type": "string",
          "description": "Placeholder text when no selection"
        },
        "size": {
          "type": "enum",
          "values": ["default", "sm"],
          "description": "size (semantic-types-v1.ttl)"
        },
        "required": {
          "type": "boolean",
          "default": false
        },
        "disabled": {
          "type": "boolean",
          "default": false
        },
        "multiple": {
          "type": "boolean",
          "default": false,
          "description": "Allow multiple selection"
        }
      }
    },
    "checkbox": {
      "category": "form",
      "interactive": true,
      "description": "Boolean toggle checkbox",
      "requiredProps": [],
      "optionalProps": ["bind", "label", "className"],
      "canHaveChildren": false,
      "tags": ["check", "toggle", "boolean"],
      "properties": {
        "bind": {
          "type": "string",
          "description": "State variable path for two-way binding"
        },
        "label": {
          "type": "string",
          "description": "Checkbox label text"
        },
        "text": {
          "type": "string",
          "description": "Alias for label"
        },
        "checked": {
          "type": "boolean",
          "default": false,
          "description": "Default checked state"
        },
        "disabled": {
          "type": "boolean",
          "default": false
        }
      }
    },
    "radio": {
      "category": "form",
      "interactive": true,
      "description": "Single-selection radio button",
      "requiredProps": [],
      "optionalProps": ["bind", "label", "group", "className"],
      "canHaveChildren": false,
      "tags": ["radio-button", "option"]
    },
    "switch": {
      "category": "form",
      "interactive": true,
      "description": "Toggle switch for on/off state",
      "requiredProps": [],
      "optionalProps": ["bind", "label", "className"],
      "canHaveChildren": false,
      "tags": ["toggle", "on-off"]
    },
    "slider": {
      "category": "form",
      "interactive": true,
      "description": "Draggable slider for numeric range",
      "requiredProps": [],
      "optionalProps": ["bind", "min", "max", "step", "className"],
      "canHaveChildren": false,
      "tags": ["range", "seek"]
    },
    "form": {
      "category": "smart",
      "description": "Form container — validation, error display, submit handling built-in",
      "builtinState": ["isValid", "errors", "isSubmitting"],
      "preferOver": "container with manual validation flow before submit",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form-group", "fieldset"]
    },
    "label": {
      "category": "form",
      "description": "Label element for form fields",
      "requiredProps": [],
      "optionalProps": ["text", "for", "className"],
      "canHaveChildren": false,
      "tags": ["field-label"]
    },
    "data-table": {
      "category": "smart",
      "description": "Data table with sorting, pagination, filtering built-in",
      "builtinState": [
        "sortColumn",
        "sortDirection",
        "currentPage",
        "pageSize"
      ],
      "preferOver": "container with manual table rows and sort/pagination flow",
      "requiredProps": ["columns"],
      "optionalProps": [
        "data",
        "loading",
        "emptyTitle",
        "emptySub",
        "stickyHeader",
        "className",
        "rowClassRules"
      ],
      "canHaveChildren": false,
      "columnTypesNote": "LEGACY/INERT (M6-R1 repair, 2026-08-10, docs/superpowers/reports/2026-08-10-m7-attribution-loops/M6-L2-attribution.md) -- superseded by columnSchema.type.values, which is the field the checker (stage-semantic/element-types.ts) and the LLM-facing prompt (prompt-builders.ts renderComponentTypes()) both read. This flat list is missing values columnSchema.type.values has (number, date) and no cogo-validator code path reads it anymore -- kept only so an external reader is not silently broken by its removal. Fix-round-1 note: the last known in-repo reader (scripts/ingest-ui-elements.ts) now reads columnSchema.type.values; a repo-wide parity gate blocks new columnTypes readers.",
      "columnTypes": [
        "text",
        "badge",
        "button",
        "checkbox",
        "custom",
        "icon",
        "index",
        "actions",
        "link",
        "action",
        "rowNumber"
      ],
      "columnSchemaSource": "packages/cogo-types/src/index.ts:312-349 (DataTableColumn) is the sole other declaration of this contract — transcribed here (L3 R7 repair, 2026-08-10) so ontology-loader.ts/renderComponentTypes() can teach the model the closed `format` enum instead of it guessing (observed: `format:\"currency\"`, outside the 2-value union).",
      "columnSchema": {
        "key": { "type": "string" },
        "header": { "type": "string" },
        "label": { "type": "string" },
        "type": {
          "type": "enum",
          "values": ["text", "number", "date", "badge", "checkbox", "button", "icon", "link", "index", "custom", "actions"]
        },
        "format": { "type": "enum", "values": ["number", "date"] },
        "width": { "type": "string" },
        "align": { "type": "enum", "values": ["left", "center", "right"] },
        "variantMap": { "type": "object|string" },
        "editType": { "type": "enum", "values": ["input", "select", "number", "checkbox"] },
        "sortable": { "type": "boolean" },
        "className": { "type": "string" },
        "headerClassName": { "type": "string" },
        "labelMap": { "type": "object|string" },
        "suffix": { "type": "string" },
        "icon": { "type": "string" },
        "text": { "type": "string" },
        "buttonVariant": { "type": "string" },
        "hrefKey": { "type": "string" },
        "visibleWhen": { "type": "string" },
        "template": { "type": "element" },
        "actions": { "type": "array" },
        "editable": { "type": "boolean" },
        "editOptions": { "type": "array|string" },
        "editPlaceholder": { "type": "string" },
        "editValueKey": { "type": "string" }
      },
      "derivedColumnValue": {
        "form": "#{<rows>.map(item => ({ ...item, <derivedKey>: <expression> }))}",
        "formSite": "the element's own `data` expression — `<rows>` is the collection (and any `.filter(...)` narrowing it) that would otherwise stand there alone.",
        "perColumnChannel": {
          "columnType": "custom",
          "columnKey": "template",
          "itemVar": "item"
        },
        "rule": "A column's value is read verbatim as `item[key]`, and the presentation fields (`format`, `labelMap`, `suffix`, `variantMap`) only re-present that value — none of them can compute one, and a column key that no producer creates renders nothing. A column whose displayed value is a TRANSFORM of the source row (a sign flip for outgoing amounts, a masked identifier, a composed label) therefore has to use one of the two channels that can. PREFERRED — materialise it as a real field on every row inside the element's own `data` expression, the same prop that already carries the collection, then name that field as the column's `key`. ALTERNATIVE — the per-column channel: give the column `type: \"custom\"` and a `template` element; the template's bindings are row-scoped, so `#{item.<field>}` inside it resolves against the row being rendered.",
        "readSite": "packages/cogo-react-renderer/src/renderers/data-table-renderer.tsx renderCell(): the default path is `const value = item[col.key]`, and the sibling presentation channels (`format`, `labelMap`, `suffix`, `variantMap`) only re-present it. The one branch that COMPUTES a cell value is `case \"custom\"`, which mounts `col.template` under `buildIteratedResolveSymbol(item, \"item\", rowIndex, ctx)` — a real per-column expression channel (row scope repaired 2026-08-18, d8e549d0f; render-asserted by packages/cogo-react-renderer/src/renderers/__tests__/data-table-custom-template-row-scope.test.tsx). This entry asserted the opposite until H1 (2026-08-20): the denial shipped ~16h AFTER that repair landed, and the model reads this text. `template` is also walked by the validator (io/stages/stage-structural.ts columns[i].template, stages/stage-runtime-expr.ts) and credited by the M7 ruler's renderer-derived read-key set.",
        "executorNote": "Q1 lane admission, 2026-08-19 (super-repo docs/superpowers/reports/2026-08-19-wonbanking-retest8/receipts/ANCHOR-REVIEW-evidence.md §1 txn R9 sign REAL x3, §4 list R5 masking REAL x3). The declared `form` is executed by @cogo/runtime-v2 compute-evaluator.ts (tryMapExpression → evalMapObjectLiteral) and is locked — placeholders substituted, evaluated for real — by packages/cogo-runtime-v2/src/__tests__/map-derived-display-field.test.ts. Do NOT reword `form` into a shape that executor does not run."
      },
      "ruleSchemaSource": "packages/cogo-types/src/index.ts (DataTable.rowClassRules) is the sole other declaration of this contract — mirrors columnSchema's teaching idiom (L5 rowClassRules admission, 2026-08-10, docs/superpowers/reports/2026-08-10-m7-attribution-loops/L4-r11-admission-design.md) so ontology-loader.ts/renderComponentTypes() can teach the model the row-scope expression convention instead of it guessing.",
      "ruleSchema": {
        "when": { "type": "expression — #{item.<rowField> <op> appData.<symbol>} (row scope is item.<field>; compare against appData./local./props./computed.<symbol> — never a bare row.* prefix)" },
        "className": { "type": "string — tailwind classes applied to the row when `when` evaluates truthy" }
      },
      "tags": ["grid", "spreadsheet", "list"],
      "properties": {
        "columns": {
          "type": "array",
          "required": true,
          "description": "Column definitions: [{ key, header, type, width?, sortable?, template? }]"
        },
        "data": {
          "type": "string",
          "description": "Symbol path to data array, e.g. #{tableData}"
        },
        "loading": {
          "type": "string|boolean",
          "description": "Loading state symbol or boolean"
        },
        "emptyTitle": {
          "type": "string",
          "description": "Title shown when data is empty"
        },
        "emptySub": {
          "type": "string",
          "description": "Subtitle shown when data is empty"
        },
        "stickyHeader": {
          "type": "boolean",
          "default": false
        },
        "pageSize": {
          "type": "number",
          "default": 10,
          "description": "Rows per page"
        },
        "selectable": {
          "type": "boolean",
          "default": false,
          "description": "Enable row selection checkboxes"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "striped"],
          "default": "default"
        },
        "rowClassRules": {
          "type": "array",
          "description": "Conditional row highlighting: [{ when: '#{item.<field> <op> <appData.|local.|props.|computed.><symbol>}', className }] — evaluated per row; className is appended alongside the static rowClassName when when is truthy."
        }
      }
    },
    "table": {
      "category": "table",
      "description": "Static HTML table",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["grid", "rows"]
    },
    "chart": {
      "category": "smart",
      "description": "Data visualization chart — responsive, legend, tooltip built-in",
      "builtinState": ["activeIndex", "hoveredSeries"],
      "preferOver": "container with manual SVG/canvas chart",
      "requiredProps": ["chartType"],
      "optionalProps": ["data", "xKey", "yKey", "colors", "className"],
      "canHaveChildren": false,
      "tags": ["graph", "visualization"],
      "properties": {
        "chartType": {
          "type": "enum",
          "values": ["bar", "line", "area", "pie", "donut"],
          "required": true
        },
        "data": {
          "type": "string|array",
          "description": "Symbol path to data array or inline data"
        },
        "xKey": {
          "type": "string",
          "description": "Data field for x-axis"
        },
        "yKey": {
          "type": "string",
          "description": "Data field for y-axis"
        },
        "colors": {
          "type": "array",
          "description": "Array of color strings for series"
        },
        "variant": {
          "type": "enum",
          "values": ["bar", "pie", "donut"],
          "aliasFor": "chartType",
          "description": "Alias for chartType. Both web and react-native chart renderers resolve `element.chartType ?? element.variant`, and stage-cobo-backoffice reads `variant` FIRST, so a chart carrying only `variant` renders and passes the cobo enum. `aliasFor` is the machine-readable form of that fact — `element-required-prop` reads it so the rule stops demanding the spelling the renderers deprecated at Phase 8107."
        }
      }
    },
    "badge": {
      "category": "feedback",
      "description": "Small status indicator or label tag",
      "requiredProps": [],
      "optionalProps": ["text", "variant", "className"],
      "canHaveChildren": false,
      "tags": ["tag", "chip", "label", "status"],
      "properties": {
        "text": {
          "type": "string",
          "description": "Badge text content"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "secondary", "outline", "destructive", "success", "warning"],
          "default": "default"
        },
        "size": {
          "type": "enum",
          "values": ["sm", "default", "lg"],
          "default": "default"
        }
      }
    },
    "link": {
      "category": "interactive",
      "description": "Tappable hyperlink or navigation trigger",
      "requiredProps": [],
      "optionalProps": ["text", "href", "className", "tone"],
      "canHaveChildren": true,
      "tags": ["anchor", "href", "url"],
      "properties": {
        "text": {
          "type": "string",
          "description": "Link text"
        },
        "href": {
          "type": "string",
          "description": "URL or route path"
        },
        "target": {
          "type": "enum",
          "values": ["_self", "_blank"],
          "default": "_self"
        },
        "rel": {
          "type": "string",
          "description": "Link relationship, e.g. 'noopener noreferrer'"
        },
        "tone": {
          "type": "enum",
          "values": ["default", "muted", "primary"],
          "description": "tone (semantic-types-v1.ttl)"
        }
      }
    },
    "card": {
      "category": "layout",
      "description": "Elevated surface container with rounded corners",
      "requiredProps": [],
      "optionalProps": ["className", "variant"],
      "canHaveChildren": true,
      "slots": ["header", "content", "footer"],
      "tags": ["panel", "tile", "surface"],
      "properties": {
        "padding": {
          "type": "number|string",
          "description": "Card inner padding"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "elevated", "outline", "ghost"],
          "description": "variant (semantic-types-v1.ttl)"
        }
      }
    },
    "card-header": {
      "category": "layout",
      "description": "Card header section — title, description, actions",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["card-part"]
    },
    "card-title": {
      "category": "commerce-generic",
      "description": "Primary heading of a Card. cofog:CardPattern required section.",
      "requiredProps": [],
      "optionalProps": ["text", "level", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "card", "title"]
    },
    "card-description": {
      "category": "typography",
      "description": "Card description text — muted paragraph",
      "requiredProps": [],
      "optionalProps": ["className", "text"],
      "canHaveChildren": true,
      "tags": ["card-part"]
    },
    "card-content": {
      "category": "commerce-generic",
      "description": "Body content of a Card. cofog:CardPattern required section.",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["cofog", "card", "body"]
    },
    "card-footer": {
      "category": "layout",
      "description": "Card footer section — actions, links",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["card-part"]
    },
    "card-action": {
      "category": "layout",
      "description": "Card action button area — top-right actions",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["card-part"]
    },
    "section": {
      "category": "layout",
      "description": "Semantic section wrapper",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["region"]
    },
    "header": {
      "category": "layout",
      "description": "Page or section header",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["top"]
    },
    "footer": {
      "category": "layout",
      "description": "Page or section footer",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["bottom"]
    },
    "nav": {
      "category": "layout",
      "description": "Navigation container",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["navigation"]
    },
    "main": {
      "category": "layout",
      "description": "Main content area",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["content"]
    },
    "aside": {
      "category": "layout",
      "description": "Sidebar or complementary content",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["sidebar"]
    },
    "article": {
      "category": "layout",
      "description": "Self-contained content block",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["post", "entry"]
    },
    "dialog": {
      "category": "smart",
      "description": "Modal dialog overlay — open/close, backdrop, focus trap built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with z-50 overlay and manual toggle",
      "requiredProps": [],
      "optionalProps": ["title", "open", "className"],
      "canHaveChildren": true,
      "tags": ["modal", "popup"]
    },
    "alert-dialog": {
      "category": "smart",
      "description": "Confirmation dialog — confirm/cancel, focus trap, escape key built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with z-50 overlay and manual confirm/cancel buttons",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["confirm", "prompt"]
    },
    "sheet": {
      "category": "smart",
      "description": "Slide-in panel — open/close, backdrop, slide animation built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with translate-x/y and manual toggle",
      "requiredProps": [],
      "optionalProps": ["side", "className"],
      "canHaveChildren": true,
      "tags": ["panel", "slide"]
    },
    "drawer": {
      "category": "smart",
      "description": "Slide-in drawer panel — open/close, snap points, swipe built-in",
      "builtinState": ["isOpen", "snapPoint"],
      "preferOver": "container with translate-y and manual swipe logic",
      "requiredProps": [],
      "optionalProps": ["side", "className"],
      "canHaveChildren": true,
      "tags": ["side-panel"]
    },
    "popover": {
      "category": "smart",
      "description": "Floating content anchored to trigger — positioning, open/close built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with absolute positioning and manual toggle",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["floating", "popup"]
    },
    "tooltip": {
      "category": "overlay",
      "description": "Hover tooltip with informational text",
      "requiredProps": [],
      "optionalProps": ["content", "className"],
      "canHaveChildren": true,
      "tags": ["hint", "info"]
    },
    "dropdown-menu": {
      "category": "smart",
      "description": "Dropdown menu — keyboard nav, sub-menus, a11y built-in",
      "builtinState": ["isOpen", "highlightedIndex"],
      "preferOver": "container with manual dropdown toggle and click handlers",
      "requiredProps": [],
      "optionalProps": ["items", "className", "bind"],
      "canHaveChildren": true,
      "tags": ["context-menu", "menu"]
    },
    "context-menu": {
      "category": "smart",
      "description": "Context menu — right-click triggered, keyboard nav, sub-menus, a11y built-in",
      "builtinState": ["isOpen", "highlightedIndex"],
      "preferOver": "container with manual contextmenu event handlers",
      "requiredProps": [],
      "optionalProps": ["items", "className", "defaultOpen", "bind"],
      "canHaveChildren": true,
      "tags": ["context-menu", "menu"]
    },
    "breadcrumb": {
      "category": "smart",
      "description": "Breadcrumb navigation trail — separators, truncation built-in",
      "builtinState": [],
      "preferOver": "container with text + separator elements for path",
      "requiredProps": [],
      "optionalProps": ["items", "className"],
      "canHaveChildren": true,
      "tags": ["path", "trail"]
    },
    "pagination": {
      "category": "smart",
      "description": "Page navigation controls — prev/next, page numbers, ellipsis built-in",
      "builtinState": ["currentPage", "totalPages"],
      "preferOver": "container with manual page number buttons and state",
      "requiredProps": [],
      "optionalProps": ["totalPages", "currentPage", "className"],
      "canHaveChildren": true,
      "tags": ["pager"]
    },
    "navigation-menu": {
      "category": "smart",
      "description": "Multi-level navigation menu — mega menu, hover triggers, a11y built-in",
      "builtinState": ["activeItem"],
      "preferOver": "container with manual hover dropdown submenus",
      "requiredProps": [],
      "optionalProps": ["items", "className"],
      "canHaveChildren": true,
      "tags": ["nav-bar", "mega-menu"]
    },
    "tabs": {
      "category": "smart",
      "description": "Tabbed content panels — active state, keyboard nav, a11y built-in",
      "builtinState": ["activeValue"],
      "preferOver": "container with #{_system.navigation.currentRoute} className expression",
      "requiredProps": [],
      "optionalProps": ["tabs", "defaultIndex", "className"],
      "canHaveChildren": true,
      "tags": ["tab-bar", "segmented"],
      "properties": {
        "defaultValue": {
          "type": "string",
          "description": "Default active tab value"
        },
        "defaultIndex": {
          "type": "number",
          "default": 0,
          "description": "Default active tab index"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "line"],
          "default": "default"
        },
        "bindRoute": {
          "type": "boolean",
          "default": false,
          "description": "When true, syncs active tab with _system.navigation.currentRoute and auto-navigates on change"
        }
      }
    },
    "tab-trigger": {
      "category": "navigation",
      "description": "Individual tab trigger button",
      "requiredProps": [],
      "optionalProps": ["value", "text", "icon", "className"],
      "canHaveChildren": true,
      "tags": ["tab"],
      "properties": {
        "value": {
          "type": "string",
          "description": "Tab value — used as route path when parent tabs has bindRoute=true"
        },
        "text": {
          "type": "string",
          "description": "Tab label text"
        },
        "icon": {
          "type": "string",
          "description": "Lucide icon name (kebab-case), e.g. 'layout-grid', 'calendar', 'users'"
        }
      }
    },
    "command": {
      "category": "smart",
      "description": "Command palette / search dialog — search, keyboard nav, grouping built-in",
      "builtinState": ["searchQuery", "selectedIndex"],
      "preferOver": "container with manual search input and filtered list",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["search", "palette"]
    },
    "menubar": {
      "category": "navigation",
      "description": "Horizontal menu bar",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["menu"]
    },
    "accordion": {
      "category": "smart",
      "description": "Expandable/collapsible content sections — open state, keyboard nav built-in",
      "builtinState": ["openItems"],
      "preferOver": "container with visibleWhen toggle pattern",
      "requiredProps": [],
      "optionalProps": ["items", "className"],
      "canHaveChildren": true,
      "tags": ["collapsible", "faq"]
    },
    "accordion-item": {
      "category": "interactive",
      "description": "Single accordion panel",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["panel"]
    },
    "accordion-trigger": {
      "category": "interactive",
      "description": "Clickable trigger header of an accordion-item — toggles open/closed state",
      "requiredProps": [],
      "optionalProps": ["text", "className"],
      "canHaveChildren": true,
      "tags": ["panel", "trigger"]
    },
    "accordion-content": {
      "category": "layout",
      "description": "Collapsible content body of an accordion-item — shown when trigger is active",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["panel", "content"]
    },
    "carousel": {
      "category": "smart",
      "description": "Swipeable horizontal gallery — slide nav, autoplay, indicators built-in",
      "builtinState": ["currentSlide", "isAutoPlaying"],
      "preferOver": "container with manual left/right scroll and index state",
      "requiredProps": [],
      "optionalProps": ["autoplay", "interval", "className"],
      "canHaveChildren": true,
      "tags": ["slider", "gallery"]
    },
    "collapsible": {
      "category": "smart",
      "description": "Collapsible content section — open/close toggle built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with visibleWhen and setState toggle",
      "requiredProps": [],
      "optionalProps": ["open", "className"],
      "canHaveChildren": true,
      "tags": ["toggle", "expandable"]
    },
    "hover-card": {
      "category": "smart",
      "description": "Card that appears on hover — hover delay, positioning built-in",
      "builtinState": ["isOpen"],
      "preferOver": "container with manual hover state and absolute positioning",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["preview"]
    },
    "resizable": {
      "category": "interactive",
      "description": "Resizable panel container",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["resize"]
    },
    "alert": {
      "category": "feedback",
      "description": "Notification banner for messages",
      "requiredProps": [],
      "optionalProps": ["title", "description", "variant", "className"],
      "canHaveChildren": true,
      "tags": ["notification", "warning"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["default", "destructive", "success", "warning", "info"],
          "description": "variant (semantic-types-v1.ttl)"
        }
      }
    },
    "progress": {
      "category": "feedback",
      "description": "Canonical Luma linear progress track. The unqualified track is 12px high; authored className geometry overrides that preset. Value/max default to 0/100 and children are invalid.",
      "requiredProps": [],
      "optionalProps": ["value", "max", "className", "variant"],
      "canHaveChildren": false,
      "tags": ["loading", "bar"],
      "properties": {
        "value": {
          "type": "number",
          "default": 0
        },
        "max": {
          "type": "number",
          "default": 100
        },
        "variant": {
          "type": "enum",
          "values": ["default", "success", "destructive"],
          "description": "variant (semantic-types-v1.ttl)"
        }
      }
    },
    "radial-progress": {
      "category": "feedback",
      "description": "Circular progress indicator. Defaults to value 0, max 100, and a platform-neutral 48px size.",
      "requiredProps": [],
      "optionalProps": ["value", "max", "size", "className"],
      "canHaveChildren": false,
      "tags": ["circular", "donut"],
      "properties": {
        "value": {
          "type": "number",
          "default": 0
        },
        "max": {
          "type": "number",
          "default": 100
        },
        "size": {
          "type": "number",
          "default": 48,
          "description": "Platform-neutral diameter in px"
        }
      }
    },
    "toast": {
      "category": "feedback",
      "description": "Temporary notification popup",
      "requiredProps": [],
      "optionalProps": ["message", "variant", "className"],
      "canHaveChildren": false,
      "tags": ["notification", "snackbar"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["default", "success", "destructive", "warning", "info"],
          "description": "variant (semantic-types-v1.ttl)"
        }
      }
    },
    "skeleton": {
      "category": "feedback",
      "description": "Loading placeholder skeleton",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": false,
      "tags": ["loading", "placeholder"]
    },
    "separator": {
      "category": "layout",
      "description": "Visual separator line",
      "requiredProps": [],
      "optionalProps": ["orientation", "className"],
      "canHaveChildren": false,
      "tags": ["divider", "line"]
    },
    "divider": {
      "category": "layout",
      "description": "Horizontal/vertical divider line",
      "requiredProps": [],
      "optionalProps": ["orientation", "className"],
      "canHaveChildren": false,
      "tags": ["separator", "hr"]
    },
    "date-picker": {
      "category": "smart",
      "description": "Calendar-based date selection — popup, month nav, formatting built-in",
      "builtinState": ["selectedDate", "isOpen"],
      "preferOver": "container with manual calendar grid and date state",
      "requiredProps": [],
      "optionalProps": ["bind", "placeholder", "className"],
      "canHaveChildren": false,
      "tags": ["calendar", "date"]
    },
    "calendar": {
      "category": "smart",
      "description": "Full calendar display — month navigation, date selection built-in",
      "builtinState": ["selectedDate", "currentMonth"],
      "preferOver": "container with manual month grid",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": false,
      "tags": ["schedule"]
    },
    "combobox": {
      "category": "smart",
      "description": "Searchable dropdown with autocomplete — search, keyboard nav built-in",
      "builtinState": ["selectedValue", "searchQuery", "isOpen"],
      "preferOver": "input + container with manual dropdown filter",
      "requiredProps": [],
      "optionalProps": ["bind", "options", "placeholder", "className"],
      "canHaveChildren": false,
      "tags": ["autocomplete", "search-select"]
    },
    "month-picker": {
      "category": "form",
      "interactive": true,
      "description": "Month and year selection",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": false,
      "tags": ["month", "period"]
    },
    "color-picker": {
      "category": "form",
      "interactive": true,
      "description": "Color selection control",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": false,
      "tags": ["color", "palette"]
    },
    "phone-input": {
      "category": "form",
      "interactive": true,
      "description": "Phone number input with country code",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": false,
      "tags": ["phone", "tel"]
    },
    "input-otp": {
      "category": "form",
      "interactive": true,
      "description": "One-time password input",
      "requiredProps": [],
      "optionalProps": ["bind", "length", "className"],
      "canHaveChildren": false,
      "tags": ["otp", "verification"]
    },
    "toggle": {
      "category": "form",
      "interactive": true,
      "description": "Toggle button (pressed/unpressed)",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": true,
      "tags": ["pressed"]
    },
    "toggle-group": {
      "category": "smart",
      "description": "Group of toggle buttons — single/multi select state built-in",
      "builtinState": ["selectedValues"],
      "preferOver": "multiple buttons with manual active state tracking",
      "requiredProps": [],
      "optionalProps": ["bind", "selectionMode", "className"],
      "canHaveChildren": true,
      "tags": ["button-group"],
      "properties": {
        "selectionMode": {
          "type": "enum",
          "values": ["single", "multiple"],
          "default": "single",
          "description": "Whether one or multiple toggle values may be selected"
        }
      }
    },
    "chip-input": {
      "category": "form",
      "interactive": true,
      "description": "Tag/chip input with add/remove",
      "requiredProps": [],
      "optionalProps": ["bind", "className"],
      "canHaveChildren": false,
      "tags": ["tags", "chips"]
    },
    "star-rating": {
      "category": "form",
      "interactive": true,
      "description": "Star-based rating input",
      "requiredProps": [],
      "optionalProps": ["bind", "max", "className"],
      "canHaveChildren": false,
      "tags": ["rating", "stars"]
    },
    "stepper": {
      "category": "smart",
      "description": "Multi-step wizard — step state, prev/next, completion tracking built-in",
      "builtinState": ["currentStep", "completedSteps"],
      "preferOver": "container with manual step index and visibleWhen per step",
      "requiredProps": [],
      "optionalProps": ["steps", "currentStep", "className"],
      "canHaveChildren": true,
      "tags": ["wizard", "steps"]
    },
    "list": {
      "category": "layout",
      "description": "Scrollable list container",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["feed", "items"]
    },
    "list_view": {
      "category": "layout",
      "description": "Renderer alias for source-projected list views; maps to the list renderer while preserving source type evidence.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "options",
        "iterateOver",
        "itemVar"
      ],
      "canHaveChildren": true,
      "tags": ["list", "collection", "source-alias"],
      "properties": {
        "className": {
          "type": "string",
          "description": "Layout and spacing classes."
        },
        "options": {
          "type": "object",
          "description": "List renderer options such as data, itemRef, direction, gap, and sourceTag."
        },
        "iterateOver": {
          "type": "string",
          "description": "Collection binding used by iterated list views."
        },
        "itemVar": {
          "type": "string",
          "description": "Iteration variable name."
        }
      }
    },
    "map": {
      "category": "smart",
      "description": "Static map renderer with location markers for geospatial application surfaces.",
      "requiredProps": [],
      "optionalProps": [
        "markers",
        "locations",
        "items",
        "label",
        "title",
        "text",
        "className",
        "style"
      ],
      "canHaveChildren": false,
      "tags": ["geospatial", "location", "map"],
      "properties": {
        "markers": {
          "type": "array",
          "description": "Marker records with label/title/name plus lat/lng, latitude/longitude, or x/y."
        },
        "locations": {
          "type": "array",
          "description": "Alias for markers when source code names the collection locations."
        },
        "items": {
          "type": "array",
          "description": "Alias for markers when generated data is carried as generic items."
        },
        "label": {
          "type": "string",
          "description": "Accessible map label."
        }
      }
    },
    "avatar": {
      "category": "feedback",
      "description": "User profile image or initials",
      "requiredProps": [],
      "optionalProps": ["src", "fallback", "size", "className"],
      "canHaveChildren": false,
      "tags": ["profile", "user"],
      "properties": {
        "size": {
          "type": "enum",
          "values": ["xs", "sm", "base", "lg", "xl"],
          "description": "size (semantic-types-v1.ttl)"
        }
      }
    },
    "timeline": {
      "category": "layout",
      "description": "Vertical timeline of events",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["history", "events"]
    },
    "tree": {
      "category": "smart",
      "description": "Hierarchical tree view — expand/collapse, selection, keyboard nav built-in",
      "builtinState": ["expandedNodes", "selectedNode"],
      "preferOver": "nested containers with manual expand/collapse toggle",
      "requiredProps": [],
      "optionalProps": ["items", "className"],
      "canHaveChildren": true,
      "tags": ["hierarchy", "nested"]
    },
    "sidebar": {
      "category": "navigation",
      "description": "Application sidebar with sections",
      "requiredProps": [],
      "optionalProps": ["className", "variant"],
      "canHaveChildren": true,
      "slots": ["header", "content", "footer"],
      "tags": ["side-nav"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["default", "floating", "inset"],
          "description": "variant (semantic-types-v1.ttl)"
        }
      }
    },
    "sidebar-inset": {
      "category": "navigation",
      "description": "Content area inset next to sidebar",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["content-area"]
    },
    "sidebar-trigger": {
      "category": "navigation",
      "description": "Button to toggle sidebar visibility",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": false,
      "tags": ["hamburger", "toggle"]
    },
    "block-ref": {
      "category": "composition",
      "description": "Reference to another block (composable blocks)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": false,
      "tags": ["reference", "include"]
    },
    "tabs-list": {
      "category": "layout",
      "description": "Container for tab triggers within a tabs component",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["tabs", "navigation"]
    },
    "tabs-trigger": {
      "category": "interactive",
      "description": "Clickable tab trigger that switches tab content (alias for tab-trigger)",
      "requiredProps": ["value"],
      "optionalProps": ["text", "className"],
      "canHaveChildren": false,
      "tags": ["tabs", "navigation", "trigger"]
    },
    "tabs-content": {
      "category": "layout",
      "description": "Content panel shown when corresponding tab trigger is active",
      "requiredProps": ["value"],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["tabs", "panel"]
    },
    "data_table": {
      "category": "table",
      "description": "Data table with columns, pagination, and row actions (snake_case alias)",
      "requiredProps": [],
      "optionalProps": [
        "columns",
        "data",
        "loading",
        "emptyTitle",
        "emptySub",
        "stickyHeader",
        "className",
        "rowClassRules"
      ],
      "canHaveChildren": false,
      "columnTypesNote": "LEGACY/INERT (M6-R1 repair, 2026-08-10, docs/superpowers/reports/2026-08-10-m7-attribution-loops/M6-L2-attribution.md) -- superseded by columnSchema.type.values, which is the field the checker (stage-semantic/element-types.ts) and the LLM-facing prompt (prompt-builders.ts renderComponentTypes()) both read. This flat list is missing values columnSchema.type.values has (number, date) and no cogo-validator code path reads it anymore -- kept only so an external reader is not silently broken by its removal. Fix-round-1 note: the last known in-repo reader (scripts/ingest-ui-elements.ts) now reads columnSchema.type.values; a repo-wide parity gate blocks new columnTypes readers.",
      "columnTypes": [
        "text",
        "badge",
        "button",
        "checkbox",
        "custom",
        "icon",
        "index",
        "actions",
        "link",
        "action",
        "rowNumber"
      ],
      "columnSchemaSource": "packages/cogo-types/src/index.ts:312-349 (DataTableColumn) is the sole other declaration of this contract — mirrors the `data-table` entry's columnSchema (L3 R7 repair, 2026-08-10) so this snake_case alias stays in parity.",
      "columnSchema": {
        "key": { "type": "string" },
        "header": { "type": "string" },
        "label": { "type": "string" },
        "type": {
          "type": "enum",
          "values": ["text", "number", "date", "badge", "checkbox", "button", "icon", "link", "index", "custom", "actions"]
        },
        "format": { "type": "enum", "values": ["number", "date"] },
        "width": { "type": "string" },
        "align": { "type": "enum", "values": ["left", "center", "right"] },
        "variantMap": { "type": "object|string" },
        "editType": { "type": "enum", "values": ["input", "select", "number", "checkbox"] },
        "sortable": { "type": "boolean" },
        "className": { "type": "string" },
        "headerClassName": { "type": "string" },
        "labelMap": { "type": "object|string" },
        "suffix": { "type": "string" },
        "icon": { "type": "string" },
        "text": { "type": "string" },
        "buttonVariant": { "type": "string" },
        "hrefKey": { "type": "string" },
        "visibleWhen": { "type": "string" },
        "template": { "type": "element" },
        "actions": { "type": "array" },
        "editable": { "type": "boolean" },
        "editOptions": { "type": "array|string" },
        "editPlaceholder": { "type": "string" },
        "editValueKey": { "type": "string" }
      },
      "derivedColumnValue": {
        "form": "#{<rows>.map(item => ({ ...item, <derivedKey>: <expression> }))}",
        "formSite": "the element's own `data` expression — `<rows>` is the collection (and any `.filter(...)` narrowing it) that would otherwise stand there alone.",
        "perColumnChannel": {
          "columnType": "custom",
          "columnKey": "template",
          "itemVar": "item"
        },
        "rule": "A column's value is read verbatim as `item[key]`, and the presentation fields (`format`, `labelMap`, `suffix`, `variantMap`) only re-present that value — none of them can compute one, and a column key that no producer creates renders nothing. A column whose displayed value is a TRANSFORM of the source row (a sign flip for outgoing amounts, a masked identifier, a composed label) therefore has to use one of the two channels that can. PREFERRED — materialise it as a real field on every row inside the element's own `data` expression, the same prop that already carries the collection, then name that field as the column's `key`. ALTERNATIVE — the per-column channel: give the column `type: \"custom\"` and a `template` element; the template's bindings are row-scoped, so `#{item.<field>}` inside it resolves against the row being rendered.",
        "readSite": "packages/cogo-react-renderer/src/renderers/data-table-renderer.tsx renderCell(): the default path is `const value = item[col.key]`, and the sibling presentation channels (`format`, `labelMap`, `suffix`, `variantMap`) only re-present it. The one branch that COMPUTES a cell value is `case \"custom\"`, which mounts `col.template` under `buildIteratedResolveSymbol(item, \"item\", rowIndex, ctx)` — a real per-column expression channel (row scope repaired 2026-08-18, d8e549d0f; render-asserted by packages/cogo-react-renderer/src/renderers/__tests__/data-table-custom-template-row-scope.test.tsx). This entry asserted the opposite until H1 (2026-08-20): the denial shipped ~16h AFTER that repair landed, and the model reads this text. `template` is also walked by the validator (io/stages/stage-structural.ts columns[i].template, stages/stage-runtime-expr.ts) and credited by the M7 ruler's renderer-derived read-key set.",
        "executorNote": "Mirrors the `data-table` entry's derivedColumnValue (Q1 lane admission, 2026-08-19) so this snake_case alias stays in parity. Same executor and same lock: packages/cogo-runtime-v2/src/__tests__/map-derived-display-field.test.ts."
      },
      "ruleSchemaSource": "packages/cogo-types/src/index.ts (DataTable.rowClassRules) is the sole other declaration of this contract — mirrors the `data-table` entry's ruleSchema (L5 rowClassRules admission, 2026-08-10) so this snake_case alias stays in parity.",
      "ruleSchema": {
        "when": { "type": "expression — #{item.<rowField> <op> appData.<symbol>} (row scope is item.<field>; compare against appData./local./props./computed.<symbol> — never a bare row.* prefix)" },
        "className": { "type": "string — tailwind classes applied to the row when `when` evaluates truthy" }
      },
      "tags": ["grid", "spreadsheet", "list", "table"],
      "properties": {
        "columns": {
          "type": "array",
          "required": true,
          "description": "Column definitions: [{ key, header, type, width?, sortable?, template? }]"
        },
        "data": {
          "type": "string",
          "description": "Symbol path to data array, e.g. #{tableData}"
        },
        "loading": {
          "type": "string|boolean",
          "description": "Loading state symbol or boolean"
        },
        "emptyTitle": {
          "type": "string",
          "description": "Title shown when data is empty"
        },
        "emptySub": {
          "type": "string",
          "description": "Subtitle shown when data is empty"
        },
        "stickyHeader": {
          "type": "boolean",
          "default": false
        },
        "pageSize": {
          "type": "number",
          "default": 10,
          "description": "Rows per page"
        },
        "selectable": {
          "type": "boolean",
          "default": false,
          "description": "Enable row selection checkboxes"
        },
        "variant": {
          "type": "enum",
          "values": ["default", "striped"],
          "default": "default"
        },
        "rowClassRules": {
          "type": "array",
          "description": "Conditional row highlighting: [{ when: '#{item.<field> <op> <appData.|local.|props.|computed.><symbol>}', className }] — evaluated per row; className is appended alongside the static rowClassName when when is truthy."
        }
      }
    },
    "modal": {
      "category": "overlay",
      "description": "Modal overlay dialog for focused content or actions",
      "requiredProps": [],
      "optionalProps": [
        "title",
        "visible",
        "open",
        "closable",
        "size",
        "className"
      ],
      "canHaveChildren": true,
      "tags": ["dialog", "popup", "overlay"],
      "properties": {
        "title": {
          "type": "string",
          "description": "Modal title text"
        },
        "visible": {
          "type": "string",
          "description": "Symbol path for visibility state"
        },
        "open": {
          "type": "boolean",
          "default": false,
          "description": "Whether modal is open"
        },
        "closable": {
          "type": "boolean",
          "default": true,
          "description": "Whether modal can be closed"
        },
        "size": {
          "type": "enum",
          "values": ["sm", "default", "lg", "xl", "full"],
          "default": "default"
        }
      }
    },
    "empty": {
      "category": "layout",
      "description": "Empty-state container — shown when a list/grid/feed has no items to display",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty", "placeholder", "no-data", "fallback"]
    },
    "empty-header": {
      "category": "layout",
      "description": "Header region within an empty-state (icon + title)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty"]
    },
    "empty-icon": {
      "category": "display",
      "description": "Icon/illustration slot for an empty-state. Registered as createSlotRenderer(EmptyMedia, { variant: 'icon' }) — the slot factory renders `children` recursively and this type has NO other content channel (no text, no icon prop), so the child IS the illustration. Matches shadcn's <EmptyMedia variant=\"icon\"><Icon/></EmptyMedia>.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["empty", "illustration"]
    },
    "empty-title": {
      "category": "text",
      "description": "Title text for an empty-state",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty", "heading"]
    },
    "empty-description": {
      "category": "text",
      "description": "Secondary description text for an empty-state",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty", "prose"]
    },
    "empty-content": {
      "category": "layout",
      "description": "Primary content region of an empty-state (between header and action)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty"]
    },
    "empty-media": {
      "category": "display",
      "description": "Media slot (image/video/animation) inside an empty-state",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty", "media"]
    },
    "empty-action": {
      "category": "interactive",
      "description": "Call-to-action region inside an empty-state (typically contains buttons/links)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["empty", "cta"]
    },
    "field-group": {
      "category": "form",
      "description": "Group of related form fields (semantic grouping; renders as <div> or <section>)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "fieldset", "group"]
    },
    "field-set": {
      "category": "form",
      "description": "HTML <fieldset>-equivalent — grouping of related fields with a shared legend",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "fieldset", "html"]
    },
    "field-legend": {
      "category": "form",
      "description": "Title for a field-set — HTML <legend>-equivalent",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "heading"]
    },
    "field": {
      "category": "form",
      "description": "Single form field wrapper — contains label + control + optional description",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "field", "shadcn"]
    },
    "field-content": {
      "category": "form",
      "description": "Content region within a field (contains the control — input/select/etc.)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form"]
    },
    "field-label": {
      "category": "form",
      "description": "Label for a form field — shadcn field-label variant",
      "requiredProps": [],
      "optionalProps": ["className", "htmlFor"],
      "canHaveChildren": true,
      "tags": ["form", "label"]
    },
    "field-description": {
      "category": "form",
      "description": "Descriptive / helper text for a form field",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "helper-text"]
    },
    "input-group": {
      "category": "form",
      "description": "Horizontal group of input + addons (prefix/suffix text or icon)",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["form", "input", "group"]
    },
    "input-group-addon": {
      "category": "form",
      "description": "Addon element inside input-group (prefix/suffix; icon or short text)",
      "requiredProps": [],
      "optionalProps": ["className", "position"],
      "canHaveChildren": true,
      "tags": ["form", "input", "addon"]
    },
    "image-gallery": {
      "category": "commerce-front-office",
      "description": "Image gallery for product detail — main image + thumbnail strip with select/zoom. cofo:ProductDetail required section.",
      "requiredProps": [],
      "optionalProps": ["images", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "product-detail", "image", "gallery"]
    },
    "price-display": {
      "category": "commerce-front-office",
      "description": "Price display with currency and optional strike-through (sale) + discount badge. cofo:ProductDetail required section.",
      "requiredProps": [],
      "optionalProps": ["amount", "currency", "className"],
      "canHaveChildren": false,
      "tags": ["cofo", "product-detail", "price", "currency"]
    },
    "add-to-cart-button": {
      "category": "commerce-front-office",
      "description": "Primary CTA on ProductDetail that triggers AddToCartAction (restApi to ACP + setState cart_session). cofo:ProductDetail required section.",
      "requiredProps": [],
      "optionalProps": ["label", "variant", "className"],
      "canHaveChildren": false,
      "tags": ["cofo", "product-detail", "cta", "cart"]
    },
    "cart-lines": {
      "category": "commerce-front-office",
      "description": "Cart line-item list. Iterates appData.cart.items; each line shows qty controls + remove + per-line subtotal. cofo:Cart required section (cofo:CartPatternShape).",
      "requiredProps": [],
      "optionalProps": ["lines", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "cart", "lines", "list"]
    },
    "cart-line": {
      "category": "commerce-front-office",
      "description": "Single cart line item. kind ∈ cofo:CartLineKind (product / shipping / discount / tax). quantity is a positive integer ≥ 1.",
      "requiredProps": [],
      "optionalProps": [
        "kind",
        "quantity",
        "unitPrice",
        "subTotal",
        "title",
        "image",
        "className"
      ],
      "properties": {
        "kind": {
          "type": "enum",
          "values": ["product", "shipping", "discount", "tax"],
          "default": "product"
        },
        "quantity": {
          "type": "number",
          "default": 1,
          "description": "Positive integer quantity greater than or equal to one"
        }
      },
      "canHaveChildren": true,
      "tags": ["cofo", "cart", "line", "item"]
    },
    "totals-summary": {
      "category": "commerce-front-office",
      "description": "Cart/order totals summary — subtotal + tax + shipping + grand total breakdown. cofo:Cart required section (cofo:CartPatternShape).",
      "requiredProps": [],
      "optionalProps": [
        "subtotal",
        "tax",
        "shipping",
        "total",
        "currency",
        "className"
      ],
      "canHaveChildren": true,
      "tags": ["cofo", "cart", "totals", "summary"]
    },
    "checkout-cta": {
      "category": "commerce-front-office",
      "description": "Cart→Checkout transition CTA region. Wraps the primary Proceed-to-Checkout action; disabled when cart empty allowed. cofo:Cart required section (cofo:CartPatternShape).",
      "requiredProps": [],
      "optionalProps": ["label", "variant", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "cart", "checkout", "cta"]
    },
    "step-indicator": {
      "category": "commerce-front-office",
      "description": "Checkout step progress indicator. Children are checkout-step nodes ordered as a contiguous subsequence of cofo:CheckoutStep closed-6 enum (cart → address → fulfillment → payment → review → confirmation). cofo:Checkout required section (cofo:CheckoutPatternShape).",
      "requiredProps": [],
      "optionalProps": ["currentStep", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "checkout", "step-indicator", "progress"]
    },
    "checkout-step": {
      "category": "commerce-front-office",
      "description": "Single step entry inside a step-indicator. The `step` property MUST be one of cofo:CheckoutStep closed-6 enum values (cart, address, fulfillment, payment, review, confirmation).",
      "requiredProps": [],
      "optionalProps": ["step", "label", "completed", "active", "className"],
      "canHaveChildren": false,
      "tags": ["cofo", "checkout", "step"]
    },
    "order-review": {
      "category": "commerce-front-office",
      "description": "Final order review section shown at the review step. Renders the line items + totals + shipping/payment summary just before placeOrder. cofo:Checkout required section (cofo:CheckoutPatternShape).",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": true,
      "tags": ["cofo", "checkout", "review", "summary"]
    },
    "confirmation-display": {
      "category": "commerce-front-office",
      "description": "Order confirmation display shown after a successful placeOrder. Includes order id, success messaging, and next-step CTAs. cofo:Checkout required section (cofo:CheckoutPatternShape).",
      "requiredProps": [],
      "optionalProps": ["orderId", "message", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "checkout", "confirmation"]
    },
    "product-card": {
      "category": "commerce-front-office",
      "description": "Product display card — image + title + price + action. Layout ∈ cofo:ProductCardLayout (vertical / horizontal / compact / featured).",
      "requiredProps": [],
      "optionalProps": ["layout", "title", "image", "price", "className"],
      "canHaveChildren": true,
      "tags": ["cofo", "storefront", "product-detail", "card"]
    },
    "hero-banner": {
      "category": "commerce-generic",
      "description": "Hero banner — above-the-fold container with image, video, or text background. Variant ∈ cofog:HeroVariant. cofog:HeroPattern required section.",
      "requiredProps": [],
      "optionalProps": [
        "variant",
        "backgroundImage",
        "backgroundVideo",
        "className"
      ],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["default", "centered", "split", "fullscreen"],
          "default": "default"
        }
      },
      "canHaveChildren": true,
      "tags": ["cofog", "hero", "banner"]
    },
    "headline": {
      "category": "commerce-generic",
      "description": "Primary heading — main message of a Hero or other section.",
      "requiredProps": [],
      "optionalProps": ["text", "level", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "hero", "heading"]
    },
    "subheadline": {
      "category": "commerce-generic",
      "description": "Secondary text below the headline. Optional supporting copy.",
      "requiredProps": [],
      "optionalProps": ["text", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "hero", "subheading"]
    },
    "cta-button": {
      "category": "commerce-generic",
      "description": "Call-to-action button. Position ∈ cofog:CtaPosition (top / bottom / center / inline).",
      "requiredProps": [],
      "optionalProps": ["label", "href", "position", "variant", "className"],
      "canHaveChildren": false,
      "tags": ["cofog", "cta", "button"]
    },
    "brand-logo": {
      "category": "commerce-generic",
      "description": "Brand identity element. cofog:FooterPattern required section.",
      "requiredProps": [],
      "optionalProps": ["src", "alt", "className"],
      "canHaveChildren": false,
      "tags": ["cofog", "footer", "brand"]
    },
    "copyright": {
      "category": "commerce-generic",
      "description": "Legal copyright notice. cofog:FooterPattern required section.",
      "requiredProps": [],
      "optionalProps": ["text", "year", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "footer", "copyright"]
    },
    "link-columns": {
      "category": "commerce-generic",
      "description": "Multi-column grouped link list in a Footer.",
      "requiredProps": [],
      "optionalProps": ["columns", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "footer", "links"]
    },
    "social-icons": {
      "category": "commerce-generic",
      "description": "Row of social media icon links.",
      "requiredProps": [],
      "optionalProps": ["icons", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "footer", "social"]
    },
    "logo": {
      "category": "commerce-generic",
      "description": "Brand mark within Navigation. cofog:NavigationPattern required section.",
      "requiredProps": [],
      "optionalProps": ["src", "alt", "href", "className"],
      "canHaveChildren": false,
      "tags": ["cofog", "navigation", "brand"]
    },
    "menu-list": {
      "category": "commerce-generic",
      "description": "List of MenuItems for Navigation. cofog:NavigationPattern required section.",
      "requiredProps": [],
      "optionalProps": ["items", "className"],
      "canHaveChildren": true,
      "tags": ["cofog", "navigation", "menu"]
    },
    "menu-item": {
      "category": "commerce-generic",
      "description": "Individual menu link with label + href (both required per cofog:MenuItemShape).",
      "requiredProps": [],
      "optionalProps": ["label", "href", "active", "className"],
      "canHaveChildren": false,
      "tags": ["cofog", "navigation", "link"]
    },
    "aspect-ratio": {
      "category": "smart",
      "description": "Maintains standard aspect ratio for its child component. shadcn AspectRatio equivalent.",
      "requiredProps": [],
      "optionalProps": ["ratio", "className"],
      "canHaveChildren": true,
      "tags": ["layout", "ratio", "aspect-ratio"],
      "properties": {
        "ratio": {
          "type": "enum",
          "values": ["16/9", "4/3", "1/1", "21/9"],
          "description": "Standard aspect ratio (semantic-types-v1.ttl / shadcn preset)"
        }
      }
    },
    "scroll-area": {
      "category": "layout",
      "description": "Custom styled scroll area. shadcn ScrollArea equivalent.",
      "requiredProps": [],
      "optionalProps": ["orientation", "className"],
      "canHaveChildren": true,
      "tags": ["scroll", "layout", "viewport"],
      "properties": {
        "orientation": {
          "type": "enum",
          "values": ["vertical", "horizontal", "both"],
          "default": "vertical"
        }
      }
    },
    "sonner": {
      "category": "feedback",
      "description": "A highly customizable notification toast emitter. shadcn Sonner equivalent.",
      "requiredProps": [],
      "optionalProps": ["position", "className"],
      "canHaveChildren": false,
      "tags": ["toast", "feedback", "notification"],
      "properties": {
        "position": {
          "type": "enum",
          "values": [
            "top-left",
            "top-center",
            "top-right",
            "bottom-left",
            "bottom-center",
            "bottom-right"
          ],
          "default": "bottom-right"
        }
      }
    },
    "alert-description": {
      "category": "feedback",
      "description": "Renderer-aligned alert-description element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["feedback", "alert", "description"]
    },
    "alert-dialog-action": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-action element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text",
        "variant",
        "size",
        "events",
        "disabled",
        "icon"
      ],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "action"]
    },
    "alert-dialog-cancel": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-cancel element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text",
        "variant",
        "size",
        "events",
        "disabled",
        "icon"
      ],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "cancel"]
    },
    "alert-dialog-content": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "size"],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "content"]
    },
    "alert-dialog-description": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-description element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["feedback", "alert", "dialog", "description"]
    },
    "alert-dialog-footer": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-footer element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "footer"]
    },
    "alert-dialog-header": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-header element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "header"]
    },
    "alert-dialog-title": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-title element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["feedback", "alert", "dialog", "title"]
    },
    "alert-dialog-trigger": {
      "category": "feedback",
      "description": "Renderer-aligned alert-dialog-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "events", "disabled"],
      "canHaveChildren": true,
      "tags": ["feedback", "alert", "dialog", "trigger"]
    },
    "alert-title": {
      "category": "feedback",
      "description": "Renderer-aligned alert-title element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["feedback", "alert", "title"]
    },
    "avatar-group": {
      "category": "layout",
      "description": "Renderer-aligned avatar-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["layout", "avatar", "group"]
    },
    "button-group": {
      "category": "action",
      "description": "Renderer-aligned button-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "orientation"],
      "canHaveChildren": true,
      "tags": ["action", "button", "group"]
    },
    "button-group-separator": {
      "category": "action",
      "description": "Renderer-aligned button-group-separator element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "orientation"],
      "canHaveChildren": true,
      "tags": ["action", "button", "group", "separator"]
    },
    "button-group-text": {
      "category": "action",
      "description": "Renderer-aligned button-group-text element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["action", "button", "group", "text"]
    },
    "dialog-content": {
      "category": "overlay",
      "description": "Renderer-aligned dialog-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned — dialog-renderer.tsx type-matches a `dialog-content` child by name (`allChildren.find(c => c.type === \"dialog-content\")`) to build DialogContent's title/className/body, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": true,
      "tags": ["overlay", "dialog", "content"]
    },
    "dialog-trigger": {
      "category": "overlay",
      "description": "Renderer-aligned dialog-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned — dialog-renderer.tsx type-matches a `dialog-trigger` child by name (`allChildren.find(c => c.type === \"dialog-trigger\")`) to build DialogTrigger's className/content, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": true,
      "tags": ["overlay", "dialog", "trigger"]
    },
    "collapsible-content": {
      "category": "layout",
      "description": "Renderer-aligned collapsible-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 collapsible-renderer.tsx type-matches a `collapsible-content` child by name (`children.find(c => c.type === \"collapsible-content\")`) to build CollapsibleContent's className/body, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "layout",
        "collapsible",
        "content"
      ]
    },
    "collapsible-trigger": {
      "category": "layout",
      "description": "Renderer-aligned collapsible-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 collapsible-renderer.tsx type-matches a `collapsible-trigger` child by name (`children.find(c => c.type === \"collapsible-trigger\")`) to build CollapsibleTrigger's label/className, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "layout",
        "collapsible",
        "trigger"
      ]
    },
    "popover-content": {
      "category": "overlay",
      "description": "Renderer-aligned popover-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 popover-renderer.tsx type-matches a `popover-content` child by name (`allChildren.find(c => c.type === \"popover-content\")`) to build PopoverContent's className/body, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "overlay",
        "popover",
        "content"
      ]
    },
    "popover-trigger": {
      "category": "overlay",
      "description": "Renderer-aligned popover-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 popover-renderer.tsx type-matches a `popover-trigger` child by name (`allChildren.find(c => c.type === \"popover-trigger\")`) to build PopoverTrigger's label/className, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "overlay",
        "popover",
        "trigger"
      ]
    },
    "sheet-content": {
      "category": "overlay",
      "description": "Renderer-aligned sheet-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 sheet-renderer.tsx type-matches a `sheet-content` child by name (`allChildren.find((child) => child.type === \"sheet-content\")`) to build SheetContent's className/body, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "overlay",
        "sheet",
        "content"
      ]
    },
    "sheet-trigger": {
      "category": "overlay",
      "description": "Renderer-aligned sheet-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned \u2014 sheet-renderer.tsx type-matches a `sheet-trigger` child by name (`allChildren.find((child) => child.type === \"sheet-trigger\")`) to build SheetTrigger's label/className, so this is a real, renderer-recognized child, not a placeholder.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text"
      ],
      "canHaveChildren": true,
      "tags": [
        "overlay",
        "sheet",
        "trigger"
      ]
    },
    "dropdown-menu-content": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["navigation", "dropdown", "menu", "content"]
    },
    "dropdown-menu-group": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["navigation", "dropdown", "menu", "group"]
    },
    "dropdown-menu-item": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-item element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "variant"],
      "canHaveChildren": true,
      "tags": ["navigation", "dropdown", "menu", "item"]
    },
    "dropdown-menu-label": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-label element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": true,
      "tags": ["navigation", "dropdown", "menu", "label"]
    },
    "dropdown-menu-separator": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-separator element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": false,
      "tags": ["navigation", "dropdown", "menu", "separator"]
    },
    "dropdown-menu-trigger": {
      "category": "navigation",
      "description": "Renderer-aligned dropdown-menu-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "events", "disabled"],
      "canHaveChildren": true,
      "tags": ["navigation", "dropdown", "menu", "trigger"]
    },
    "context-menu-content": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["navigation", "context-menu", "menu", "content"]
    },
    "context-menu-group": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["navigation", "context-menu", "menu", "group"]
    },
    "context-menu-item": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-item element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "variant"],
      "canHaveChildren": true,
      "tags": ["navigation", "context-menu", "menu", "item"]
    },
    "context-menu-label": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-label element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": true,
      "tags": ["navigation", "context-menu", "menu", "label"]
    },
    "context-menu-separator": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-separator element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": false,
      "tags": ["navigation", "context-menu", "menu", "separator"]
    },
    "context-menu-trigger": {
      "category": "navigation",
      "description": "Renderer-aligned context-menu-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "events"],
      "canHaveChildren": true,
      "tags": ["navigation", "context-menu", "menu", "trigger"]
    },
    "heading": {
      "category": "basic",
      "description": "Renderer-aligned heading element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["text", "level", "className", "children", "tone"],
      "canHaveChildren": true,
      "tags": ["basic", "heading"],
      "properties": {
        "level": {
          "type": "enum",
          "values": ["1", "2", "3", "4", "5", "6"],
          "description": "level (semantic-types-v1.ttl)"
        },
        "tone": {
          "type": "enum",
          "values": ["default", "muted", "primary"],
          "description": "tone (semantic-types-v1.ttl)"
        }
      }
    },
    "island": {
      "category": "integration",
      "description": "Renderer-aligned island element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["island", "props", "fallback", "className"],
      "canHaveChildren": true,
      "tags": ["integration", "island"]
    },
    "item": {
      "category": "collection",
      "description": "Renderer-aligned item element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "text", "variant", "size"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": ["default", "outline", "muted"],
          "default": "default"
        }
      },
      "canHaveChildren": true,
      "tags": ["collection", "item"]
    },
    "item-actions": {
      "category": "collection",
      "description": "Renderer-aligned item-actions element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "events"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "actions"]
    },
    "item-content": {
      "category": "collection",
      "description": "Renderer-aligned item-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "content"]
    },
    "item-description": {
      "category": "collection",
      "description": "Renderer-aligned item-description element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["collection", "item", "description"]
    },
    "item-footer": {
      "category": "collection",
      "description": "Renderer-aligned item-footer element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "footer"]
    },
    "item-group": {
      "category": "collection",
      "description": "Renderer-aligned item-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "group"]
    },
    "item-header": {
      "category": "collection",
      "description": "Renderer-aligned item-header element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "header"]
    },
    "item-media": {
      "category": "collection",
      "description": "Renderer-aligned item-media element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "variant"],
      "canHaveChildren": true,
      "tags": ["collection", "item", "media"]
    },
    "item-separator": {
      "category": "collection",
      "description": "Renderer-aligned item-separator element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": false,
      "tags": ["collection", "item", "separator"]
    },
    "item-title": {
      "category": "collection",
      "description": "Renderer-aligned item-title element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": false,
      "tags": ["collection", "item", "title"]
    },
    "kbd": {
      "category": "basic",
      "description": "Renderer-aligned kbd element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned. Children are a CONTENT channel, not decoration: KbdRenderer branches on `element.children.length > 0` and renders a KbdGroup of one Kbd per child (kbd-renderer.tsx). A multi-key shortcut is authored as a kbd whose children are kbds; strip them and the element renders empty, because the parent carries no `text`.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text"],
      "canHaveChildren": true,
      "tags": ["basic", "kbd"]
    },
    "loader": {
      "category": "feedback",
      "description": "Renderer-aligned loader element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "size"],
      "canHaveChildren": false,
      "tags": ["feedback", "loader"]
    },
    "lottie": {
      "category": "media",
      "description": "Renderer-aligned lottie element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["src", "autoplay", "loop", "className"],
      "canHaveChildren": false,
      "tags": ["media", "lottie"]
    },
    "native-select": {
      "category": "input",
      "description": "Renderer-aligned native-select element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "size",
        "value",
        "defaultValue",
        "bind"
      ],
      "canHaveChildren": true,
      "tags": ["input", "native", "select"]
    },
    "native-select-wrapper": {
      "category": "input",
      "description": "Renderer-aligned native-select-wrapper element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["input", "native", "select", "wrapper"]
    },
    "placeholder": {
      "category": "feedback",
      "description": "Renderer-aligned placeholder element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "text", "variant", "size"],
      "properties": {
        "variant": {
          "type": "enum",
          "values": [
            "block-generating",
            "data-loading",
            "image-pending",
            "variable-unbound",
            "content-empty"
          ],
          "default": "block-generating"
        }
      },
      "canHaveChildren": false,
      "tags": ["feedback", "placeholder"]
    },
    "progress-indicator": {
      "category": "feedback",
      "description": "Leaf progress fill indicator. Width is value/max percent of its containing track; it inherits the track radius unless explicitly authored, and authored children are invalid.",
      "requiredProps": [],
      "optionalProps": ["value", "max", "className"],
      "canHaveChildren": false,
      "tags": ["feedback", "progress", "indicator"],
      "properties": {
        "value": {
          "type": "number",
          "default": 0
        },
        "max": {
          "type": "number",
          "default": 100
        }
      }
    },
    "radio-group": {
      "category": "input",
      "description": "Renderer-aligned radio-group element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text",
        "value",
        "defaultValue",
        "bind",
        "disabled"
      ],
      "canHaveChildren": true,
      "tags": ["input", "radio", "group"]
    },
    "radio-group-item": {
      "category": "input",
      "description": "Renderer-aligned radio-group-item element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": [
        "className",
        "children",
        "text",
        "size",
        "value",
        "disabled"
      ],
      "canHaveChildren": true,
      "tags": ["input", "radio", "group", "item"]
    },
    "ring-progress": {
      "category": "feedback",
      "description": "Compact circular progress indicator. Defaults to value 0, max 100, and a platform-neutral 16px size.",
      "requiredProps": [],
      "optionalProps": ["value", "max", "size", "className"],
      "canHaveChildren": false,
      "tags": ["feedback", "ring", "progress"],
      "properties": {
        "value": {
          "type": "number",
          "default": 0
        },
        "max": {
          "type": "number",
          "default": 100
        },
        "size": {
          "type": "number",
          "default": 16,
          "description": "Platform-neutral diameter in px"
        }
      }
    },
    "select-content": {
      "category": "input",
      "description": "Renderer-aligned select-content element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["input", "select", "content"]
    },
    "select-trigger": {
      "category": "input",
      "description": "Renderer-aligned select-trigger element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "events", "disabled"],
      "canHaveChildren": true,
      "tags": ["input", "select", "trigger"]
    },
    "table-body": {
      "category": "data",
      "description": "Renderer-aligned table-body element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["data", "table", "body"]
    },
    "table-cell": {
      "category": "data",
      "description": "Renderer-aligned table-cell element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "colSpan", "align"],
      "canHaveChildren": true,
      "tags": ["data", "table", "cell"]
    },
    "table-footer": {
      "category": "data",
      "description": "Renderer-aligned table-footer element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["data", "table", "footer"]
    },
    "table-head": {
      "category": "data",
      "description": "Renderer-aligned table-head element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "colSpan", "align"],
      "canHaveChildren": true,
      "tags": ["data", "table", "head"]
    },
    "table-header": {
      "category": "data",
      "description": "Renderer-aligned table-header element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["data", "table", "header"]
    },
    "table-row": {
      "category": "data",
      "description": "Renderer-aligned table-row element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children"],
      "canHaveChildren": true,
      "tags": ["data", "table", "row"]
    },
    "toggle-group-item": {
      "category": "action",
      "description": "Renderer-aligned toggle-group-item element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className", "children", "text", "variant", "size"],
      "canHaveChildren": true,
      "tags": ["action", "toggle", "group", "item"]
    },
    "waveform": {
      "category": "media",
      "description": "Renderer-aligned waveform element. Added from @cogo/react-renderer registry parity so Cogo JSON, ontology, and renderer registration stay aligned.",
      "requiredProps": [],
      "optionalProps": ["className"],
      "canHaveChildren": false,
      "tags": ["media", "waveform"]
    }
  }
}
